/**
 * CookieBanner.tsx — Banner de consentimento LGPD/GDPR × 5 idiomas (React).
 *
 * Exibe apenas quando o usuário ainda não decidiu (localStorage vazio).
 * Estética retro Dr. Pera: Plus Jakarta Sans + paleta ink/coral/green-fluor.
 *
 * Props:
 *   - t: função de tradução do useTranslations() do App raiz
 *   - locale: idioma ativo (para URL da página de privacidade)
 *
 * Conformidade:
 *   - LGPD (Lei 13.709/2018): consentimento livre, informado, inequívoco
 *   - GDPR (Reg. UE 2016/679): consentimento granular + recusa fácil
 *   - "APENAS ESSENCIAIS" → GA4 nunca carrega
 */
import { useState, useEffect, useCallback } from 'react';
import { getConsent, setConsent } from '../utils/cookieConsent';
import type { Locale } from '../types';

interface CookieBannerProps {
  /** Função de tradução — do useTranslations() do App */
  t: (key: string) => string;
  /** Idioma ativo — determina URL da página de privacidade */
  locale: Locale;
}

/** Mapa de URL da página de privacidade por locale */
const PRIVACY_URLS: Record<Locale, string> = {
  'pt-BR': 'https://drpera.com.br/privacidade/',
  es: 'https://drpera.com.br/es/privacidad/',
  it: 'https://drpera.com.br/it/privacy/',
  fr: 'https://drpera.com.br/fr/confidentialite/',
  en: 'https://drpera.com.br/en/privacy/',
};

export function CookieBanner({ t, locale }: CookieBannerProps): JSX.Element | null {
  /* null = ainda carregando | 'granted'/'denied' = já decidiu → não mostra */
  const [mostrar, setMostrar] = useState<boolean>(false);

  useEffect(() => {
    /* Verifica localStorage após montar (SSR-safe) */
    const status = getConsent();
    if (status === null) {
      setMostrar(true);
    }
  }, []);

  const handleAceitar = useCallback(() => {
    setConsent('granted');
    setMostrar(false);
  }, []);

  const handleRecusar = useCallback(() => {
    setConsent('denied');
    setMostrar(false);
  }, []);

  if (!mostrar) return null;

  const urlPrivacidade = PRIVACY_URLS[locale] ?? PRIVACY_URLS['pt-BR'];

  return (
    <div
      role="dialog"
      aria-modal={false}
      aria-label={t('cookies.title')}
      style={estilos.banner}
    >
      <div style={estilos.inner}>
        {/* Texto informativo */}
        <p style={estilos.texto}>
          <span style={estilos.titulo}>&#9658; {t('cookies.title')}</span>
          <span style={estilos.descricao}>
            {t('cookies.description')}{' '}
            <a
              href={urlPrivacidade}
              target="_blank"
              rel="noopener noreferrer"
              style={estilos.link}
            >
              {t('cookies.learnMore')}
            </a>
          </span>
        </p>

        {/* Botões de decisão */}
        <div style={estilos.botoes}>
          <button
            type="button"
            onClick={handleAceitar}
            aria-label={t('cookies.accept')}
            style={{ ...estilos.btn, ...estilos.btnGreen }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.boxShadow =
                '0 0 14px rgba(0, 96, 240, 0.7), 0 0 28px rgba(0, 96, 240, 0.4)';
              (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
              (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
            }}
          >
            [ {t('cookies.accept')} ]
          </button>

          <button
            type="button"
            onClick={handleRecusar}
            aria-label={t('cookies.decline')}
            style={{ ...estilos.btn, ...estilos.btnCoral }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                'rgba(124, 124, 248, 0.08)';
              (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
            }}
          >
            [ {t('cookies.decline')} ]
          </button>
        </div>
      </div>
    </div>
  );
}

/** Estilos inline — evita dependência de CSS externo, consistente com padrão do /ia */
const estilos: Record<string, React.CSSProperties> = {
  banner: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    background: 'var(--ink-soft, #2A2C38)',
    borderTop: '2px solid var(--violet, #7C7CF8)',
    padding: '16px 24px',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.4)',
  },
  inner: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '24px',
    flexWrap: 'wrap' as const,
  },
  texto: {
    flex: 1,
    minWidth: '280px',
    margin: 0,
  },
  titulo: {
    display: 'block',
    fontFamily: "var(--font-sans, 'Plus Jakarta Sans', sans-serif)",
    fontSize: '14px',
    fontWeight: 600,
    color: '#FFFFFF',
    marginBottom: '8px',
    letterSpacing: '0.04em',
  },
  descricao: {
    fontFamily: "var(--font-sans, 'Plus Jakarta Sans', sans-serif)",
    fontSize: '14px',
    color: 'var(--ink-light, #C7C8CE)',
    lineHeight: '1.6',
  },
  link: {
    color: 'var(--violet, #7C7CF8)',
    textDecoration: 'underline',
  },
  botoes: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap' as const,
    flexShrink: 0,
  },
  btn: {
    fontFamily: "var(--font-sans, 'Plus Jakarta Sans', sans-serif)",
    fontSize: '12px',
    fontWeight: 600,
    padding: '10px 18px',
    cursor: 'pointer',
    background: 'transparent',
    letterSpacing: '0.03em',
    lineHeight: '1.4',
    transition: 'box-shadow 180ms ease, transform 100ms ease, background 150ms ease',
  },
  btnGreen: {
    border: '1px solid var(--cta-blue, #0060F0)',
    background: 'var(--cta-blue, #0060F0)',
    color: '#FFFFFF',
  },
  btnCoral: {
    border: '1px solid var(--violet, #7C7CF8)',
    color: 'var(--violet, #7C7CF8)',
  },
};
